import React, { useState, useEffect, useMemo } from 'react';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { addActivity } from '../utils/cardUtils';

import {
    ModalOverlay,
    ModalContent,
    ModalHeader,
    SearchInput,
    ParentListContainer,
    ParentListItem,
    NoResultsMessage,
    ModalActions,
    CancelButton,
    // FieldLabel // Uncomment if using a label for the search input
} from '../styles/SetParentModal.styles';

const SetParentModal = ({
    isOpen,
    onClose,
    projectId,
    currentCardId,
    // currentCardTitle, // Not directly used in this modal's core logic but good for context
    allColumns,
    onParentSet, // Callback: (parentCard) => void
}) => {
    const { user } = useAuth();
    const [searchTerm, setSearchTerm] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setSearchTerm(''); // Reset search term when modal opens
        }
    }, [isOpen]);

    const findCardById = (cardIdToFind) => {
        if (!allColumns || !cardIdToFind) return null;
        for (const columnId in allColumns) {
            const column = allColumns[columnId];
            if (column.cards) {
                const foundCard = column.cards.find(c => c.id === cardIdToFind);
                if (foundCard) return foundCard;
            }
        }
        return null;
    };

    const getAllDescendantIds = (cardId, currentAllColumns) => {
        let descendants = new Set();
        if (!cardId || !currentAllColumns) return descendants;
        let queue = [cardId];
        const visitedInTraversal = new Set();
        while (queue.length > 0) {
            const currentId = queue.shift();
            if (visitedInTraversal.has(currentId) && currentId !== cardId) continue;
            visitedInTraversal.add(currentId);
            for (const colId in currentAllColumns) {
                const column = currentAllColumns[colId];
                if (column.cards) {
                    for (const card of column.cards) {
                        if (card.parentId === currentId) {
                            if (!descendants.has(card.id)) {
                                descendants.add(card.id);
                                queue.push(card.id);
                            }
                        }
                    }
                }
            }
        }
        return descendants;
    };

    const availableParentCards = useMemo(() => {
        if (!allColumns || !currentCardId) return [];
        const descendants = getAllDescendantIds(currentCardId, allColumns);
        const allFlattenedCards = [];
        for (const columnId in allColumns) {
            if (allColumns[columnId] && allColumns[columnId].cards) {
                allFlattenedCards.push(...allColumns[columnId].cards);
            }
        }
        return allFlattenedCards.filter(card => {
            if (!card || !card.id) return false;
            return card.id !== currentCardId && !descendants.has(card.id);
        });
    }, [allColumns, currentCardId]);

    const filteredParentCandidates = useMemo(() => {
        if (!searchTerm) {
            return availableParentCards;
        }
        return availableParentCards.filter(card =>
            card.title.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [availableParentCards, searchTerm]);

    const handleSelectParent = async (selectedParentCard) => {
        if (!selectedParentCard || isSaving) return;

        setIsSaving(true);
        try {
            const projectRef = doc(db, 'projects', projectId);
            // Fetch the project document to get the current state of the card being modified.
            // This ensures we are updating the correct version of the card.
            const projectSnap = await getDoc(projectRef);
            if (!projectSnap.exists()) {
                throw new Error("Project not found");
            }
            const projectData = projectSnap.data();
            let cardToUpdate = null;
            let cardColumnId = null;

            // Find the current card in the project data
            for (const colId in projectData.columns) {
                const column = projectData.columns[colId];
                const foundCard = column.cards.find(c => c.id === currentCardId);
                if (foundCard) {
                    cardToUpdate = foundCard;
                    cardColumnId = colId;
                    break;
                }
            }

            if (!cardToUpdate) {
                throw new Error("Current card not found in project data. Cannot set parent.");
            }

            const newActivityForParent = addActivity(user, {
                type: 'parent_set',
                details: `set parent to "${selectedParentCard.title}" (ID: ${selectedParentCard.id})`
            });

            const updatedCard = {
                ...cardToUpdate,
                parentId: selectedParentCard.id,
                activity: [...(cardToUpdate.activity || []), newActivityForParent]
            };

            const updatedColumnCards = projectData.columns[cardColumnId].cards.map(c =>
                c.id === currentCardId ? updatedCard : c
            );

            await updateDoc(projectRef, {
                [`columns.${cardColumnId}.cards`]: updatedColumnCards,
                updatedAt: new Date().toISOString()
            });

            if (onParentSet) {
                onParentSet(updatedCard); // Pass the fully updated card object
            }
            onClose();
        } catch (error) {
            console.error('Error setting parent task:', error);
            alert('Failed to set parent task. ' + error.message);
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <ModalOverlay onClick={onClose}>
            <ModalContent onClick={e => e.stopPropagation()}>
                <ModalHeader>Set Parent Task</ModalHeader>
                {/* <FieldLabel htmlFor="parentSearch">Search for a parent card</FieldLabel> */}
                <SearchInput
                    id="parentSearch"
                    type="text"
                    placeholder="Search cards by title..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    autoFocus
                />
                <ParentListContainer>
                    {filteredParentCandidates.length > 0 ? (
                        filteredParentCandidates.map(card => (
                            <ParentListItem
                                key={card.id}
                                onClick={() => handleSelectParent(card)}
                                title={card.title}
                                disabled={isSaving}
                            >
                                {card.title}
                            </ParentListItem>
                        ))
                    ) : (
                        <NoResultsMessage>
                            {searchTerm ? "No matching cards found." : "No available parent cards."}
                        </NoResultsMessage>
                    )}
                </ParentListContainer>
                <ModalActions>
                    <CancelButton onClick={onClose} disabled={isSaving}>Cancel</CancelButton>
                    {/* No explicit "Set Parent" button, selection from list triggers action */}
                </ModalActions>
            </ModalContent>
        </ModalOverlay>
    );
};

export default SetParentModal;
